import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { environment } from '../../../environments/environment';
import { LoadingService } from '../../Services/Loading/loading-service.service';
import { AchAuthorizationDialogComponent } from '../ach-authorization-dialog/ach-authorization-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { AddressServiceService } from '../../Services/address-service.service';

declare let Chargify: any;

@Component({
    selector: 'app-chargify-form',
    templateUrl: './chargify-form.component.html',
    styleUrls: ['./chargify-form.component.scss'],
    standalone: false
})
/* istanbul ignore next */
export class ChargifyFormComponent {
  @Input() type: string = 'card';
  @ViewChild('form', { static: true }) chargifyForm: ElementRef | undefined;
  chargify: any;
  token: string = '';
  paymentMethod: string = 'cc';
  isAch: boolean = false;
  achTermsAccepted: boolean = false;
  @Input() usePropertyDetails: boolean = false;
  @Input() firstName: string = '';
  @Input() lastName: string = '';
  @Input() address: string = '';
  @Input() city: string = '';
  @Input() state: string = '';
  @Input() zip: string = '';
  @Input() country: string = 'US';
  isChargifyInitialized: boolean = false;
  @Output() validationSuccess = new EventEmitter<string>();
  @Output() validationFailure = new EventEmitter<void>();
  @Output() achTermsAcceptedChange = new EventEmitter<boolean>();
  @Output() isAchChange = new EventEmitter<boolean>();
  paymentError: string | null = null;
  @Output() achAgreementComplete = new EventEmitter<any>();
  achAgreement: any = null;
  billingAddress: string = '';
  billingCity: string = '';
  billingState: string = '';
  billingZip: string = '';
  billingAddressError: string | null = null;
  addressSuggestions: any[] = [];
  validatedBillingAddress: string = '';
  validatedBillingCity: string = '';
  validatedBillingState: string = '';
  validatedBillingZip: string = '';

  /* istanbul ignore next */
  constructor(
    readonly loadingservice: LoadingService,
    readonly dialog: MatDialog,
    private readonly addressService: AddressServiceService
  ) {}
  /* istanbul ignore next */
  ngOnInit() {
    if (!this.isChargifyInitialized) {
      this.loadChargifyForm();
    }
  }
  /* istanbul ignore next */
  onAddressInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const address = inputElement.value;

    if (address.length > 5) {
      this.fetchAddressSuggestionsChargify(address);
    }
  }
  /* istanbul ignore next */
  fetchAddressSuggestionsChargify(address: string): void {
    this.addressService.fetchFilteredAddressSuggestions(address).subscribe({
      next: (suggestions) => {
        // Further filter to include only full addresses
        this.addressSuggestions = suggestions.filter((suggestion: any) =>
          this.isFullAddress(suggestion.address)
        );
      },
      error: (error) => {
        console.error('Failed to fetch address suggestions Component:', error);
      },
    });
  }
  /* istanbul ignore next */
  selectSuggestion(suggestion: any): void {
    if (this.isFullAddress(suggestion.address)) {
      this.updateFormWithAddressChargify(suggestion.address);
      this.addressSuggestions = [];
    } else {
      console.error('Invalid address selected:', suggestion.address);
    }
  }
  /* istanbul ignore next */
  enterManualAddress(): void {
    this.addressSuggestions = [];
  }
  /* istanbul ignore next */
  isFullAddress(address: any): boolean {
    return (
      address.streetNumber &&
      address.streetName &&
      address.municipality &&
      address.postalCode &&
      address.countryCode
    );
  }

  /* istanbul ignore next */
  updateFormWithAddressChargify(address: any): void {
    this.address = `${address.streetNumber} ${address.streetName}`;
    this.city = address.municipality;
    this.state = address.countrySubdivisionCode;
    this.zip = address.postalCode.substring(0, 5);
    this.country = address.countryCode;
  }
  /* istanbul ignore next */
  ngOnChanges(changes: SimpleChanges) {
    let shouldReload = false;

    if (
      changes['firstName']?.currentValue !==
        changes['firstName']?.previousValue ||
      changes['lastName']?.currentValue !==
        changes['lastName']?.previousValue ||
      changes['address']?.currentValue !== changes['address']?.previousValue ||
      changes['city']?.currentValue !== changes['city']?.previousValue ||
      changes['state']?.currentValue !== changes['state']?.previousValue ||
      changes['zip']?.currentValue !== changes['zip']?.previousValue ||
      changes['country']?.currentValue !== changes['country']?.previousValue
    ) {
      shouldReload = true;
    }

    if (shouldReload) {
      console.log('Inputs changed. Reloading Chargify form...');
      this.reloadChargifyForm();
    }
  }
  /* istanbul ignore next */
  togglePaymentMethod() {
    this.paymentError = null;
    this.loadingservice.show();
    this.isAchChange.emit(this.isAch);
    this.reloadPaymentSpecificFields();
    this.loadingservice.hide();
  }
  /* istanbul ignore next */
  reloadChargifyForm() {
    console.log('Reloading Chargify form...');

    if (this.chargify) {
      this.chargify.unload();
      console.log('Previous Chargify form unloaded.');
    }

    this.loadChargifyForm();
  }
  /* istanbul ignore next */
  reloadPaymentSpecificFields() {
    console.log('Reloading payment-specific fields...');
    const fields = {
      ...this.getPaymentSpecificFields(),
    };

    if (this.chargify) {
      this.chargify.unload();
      console.log('Previous Chargify form unloaded.');
    }

    this.chargify.load({
      selector: '#chargify-form',
      publicKey: environment.chargifyPublicToken,
      type: this.isAch ? 'bank' : 'card',
      serverHost: environment.serverHost,
      fields: fields, // Only include payment-specific fields
      style: {
        input: {
          width: '100%',
          backgroundColor: '#fff',
          color: '#333',
          fontSize: '12px',
          border: '1px solid #ccc',
        },
        label: {
          color: '#555',
          fontSize: '14px',
          marginBottom: '4px',
        },
        message: { color: '#f44336', fontSize: '12px' },
      },
    });

    console.log('Form reloaded with updated fields.');
  }
  /* istanbul ignore next */
  clearPaymentError() {
    this.paymentError = null;
  }
  /* istanbul ignore next */
  onAchTermsAcceptedChange(status: boolean) {
    this.achTermsAccepted = status;
    console.log('ACH Terms Accepted:', this.achTermsAccepted);
  }
  /* istanbul ignore next */
  onAchTermsCheckboxChange(event: any) {
    if (this.isAch && event.checked) {
      this.openAchAgreementDialog();
    } else {
      this.achTermsAccepted = false;
      this.achTermsAcceptedChange.emit(false);
    }
  }
  /* istanbul ignore next */
  openAchAgreementDialog() {
    // Fetch the user's IP address before opening the dialog
    this.addressService.getIpAddress().subscribe({
      next: (response) => {
        const ipAddress = response.ip; // Extract the IP address
        console.log('User IP Address:', ipAddress);

        // Pass the IP address to the dialog
        const dialogRef = this.dialog.open(AchAuthorizationDialogComponent, {
          width: '600px',
          data: { ipAddress }, // Pass IP address as data
        });

        dialogRef.afterClosed().subscribe((result: any) => {
          if (result) {
            this.achTermsAccepted = true;
            this.achAgreement = result;
            this.achTermsAcceptedChange.emit(true);
            this.achAgreementComplete.emit(result);
            console.log('ACH Agreement accepted:', this.achAgreement);
          } else {
            this.achTermsAccepted = false;
            this.achTermsAcceptedChange.emit(false);
          }
        });
      },
      error: (error) => {
        console.error('Failed to fetch IP address:', error);
        // Handle the error (e.g., show a message to the user)
      },
    });
  }
  /* istanbul ignore next */
  onBillingOptionChange() {
    if (this.usePropertyDetails) {
      this.billingAddress = this.address;
      this.billingCity = this.city;
      this.billingState = this.state;
      this.billingZip = this.zip;
      this.billingAddressError = null;
    } else {
      this.billingAddress = '';
      this.billingCity = '';
      this.billingState = '';
      this.billingZip = '';
    }
  }
  /* istanbul ignore next */
  validateZipCode(): boolean {
    const zipPattern = /^\d{5}$/;
    return zipPattern.test(this.zip);
  }
  /* istanbul ignore next */ /* istanbul ignore next */
  loadChargifyForm() {
    this.loadingservice.show();
    this.chargify = new Chargify();
    this.isChargifyInitialized = true;

    const fields = {
      ...this.getPaymentSpecificFields(),
    };

    this.chargify.load({
      selector: '#chargify-form',
      publicKey: environment.chargifyPublicToken,
      type: this.isAch ? 'bank' : 'card',
      serverHost: environment.serverHost,
      fields: fields, // Only include payment-specific fields
      style: {
        input: {
          width: '100%',
          backgroundColor: '#fff',
          color: '#333',
          fontSize: '12px',
          border: '1px solid #ccc',
          borderRadius: '4px',
        },
        label: {
          color: '#555',
          fontSize: '14px',
          marginBottom: '4px',
        },
        message: {
          color: '#f44336',
          fontSize: '12px',
        },
      },
    });

    console.log('Chargify form loaded with payment-specific fields.');
    this.loadingservice.hide();
  }
  /* istanbul ignore next */
  validateChargifyForm() {
    // Clear previous errors
    this.paymentError = null;

    // Validate all required fields manually
    const missingFields = [];
    if (!this.firstName) missingFields.push('First Name');
    if (!this.lastName) missingFields.push('Last Name');
    if (!this.address) missingFields.push('Address');
    if (!this.city) missingFields.push('City');
    if (!this.state) missingFields.push('State');
    if (!this.zip || !this.validateZipCode()) missingFields.push('ZIP Code');

    if (missingFields.length > 0) {
      this.paymentError = `Please fill out the following fields: ${missingFields.join(
        ', '
      )}`;
      console.error(this.paymentError);
      this.validationFailure.emit(); // Emit validation failure event
      return;
    }

    // Proceed with Chargify token generation if validation passes
    this.chargify.token(
      this.chargifyForm?.nativeElement,
      (token: string) => {
        console.log('Chargify token generated:', token);
        this.token = token;
        this.validationSuccess.emit(token);
      },
      (error: any) => {
        console.error('Chargify validation errors:', error);
        this.paymentError = error.errors ?? 'An unknown error occurred.';
        this.validationFailure.emit();
      }
    );
  }
  /* istanbul ignore next */
  clearError(field: string) {
    if (field === 'lastName' && this.lastName) {
      this.paymentError = null; // Clear error if last name is now valid
    }
  }
  /* istanbul ignore next */
  handleSubmit() {
    this.paymentError = null;
    this.chargify.token(
      this.chargifyForm?.nativeElement,
      (token: any) => {
        console.log('{host} token SUCCESS - token: ', token);
        this.token = token;
      },
      (error: any) => {
        console.log('{host} token ERROR - err: ', error);
        this.paymentError = error.errors ?? 'An unknown error occurred.';
      }
    );
  }

  ngOnDestroy() {
    if (this.chargify) {
      this.chargify.unload();
      console.log('Chargify form unloaded during component destruction.');
    } else {
      console.warn('Chargify is not initialized. Skipping unload.');
    }
  }

  private getPaymentSpecificFields() {
    return this.isAch
      ? {
          bankName: {
            selector: '#chargify-bank-name',
            label: 'Bank Name',
            required: true,
          },
          routingNumber: {
            selector: '#chargify-bank-routing-number',
            label: 'Routing Number',
            required: true,
          },
          accountNumber: {
            selector: '#chargify-bank-account-number',
            label: 'Account Number',
            required: true,
          },
          accountHolderType: {
            selector: '#chargify-bank-accountholder-type',
            label: 'Account Type',
            required: true,
          },
        }
      : {
          number: {
            selector: '#chargify-number',
            label: 'Card Number',
            required: true,
          },
          month: {
            selector: '#chargify-month',
            label: 'Expiration Month',
            required: true,
          },
          year: {
            selector: '#chargify-year',
            label: 'Expiration Year',
            required: true,
          },
          cvv: { selector: '#chargify-cvv', label: 'CVV', required: true },
        };
  }
}
